import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo } from "react";

interface TagSuggestion {
  tag_name: string;
  source: "preset" | "user_history" | "popular";
  count?: number;
}

export function useTagSuggestions(
  bookId: string,
  userId: string | undefined,
  searchQuery: string
) {
  // Fetch preset tags
  const { data: presetTags = [] } = useQuery({
    queryKey: ["preset-tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("preset_tags")
        .select("tag_name")
        .order("sort_order");

      if (error) throw error;
      return data.map((t) => t.tag_name);
    },
  });

  // Fetch user's tag history
  const { data: userHistory = [] } = useQuery({
    queryKey: ["user-tag-history", userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from("book_tags")
        .select("tag_name")
        .eq("user_id", userId);

      if (error) throw error;

      // Get unique tags
      const uniqueTags = [...new Set(data.map((t) => t.tag_name))];
      return uniqueTags;
    },
    enabled: !!userId,
  });

  // Fetch popular tags for this book
  const { data: popularTags = [] } = useQuery({
    queryKey: ["book-tags-popular", bookId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("book_tags")
        .select("tag_name")
        .eq("book_id", bookId);

      if (error) throw error;

      // Count occurrences
      const tagCounts = data.reduce((acc, { tag_name }) => {
        acc[tag_name] = (acc[tag_name] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return Object.entries(tagCounts)
        .map(([tag_name, count]) => ({ tag_name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);
    },
    enabled: !!bookId,
  });

  // Combine and filter suggestions
  const suggestions = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const allSuggestions: TagSuggestion[] = [];

    // Add preset tags
    presetTags.forEach((tag) => {
      if (tag.toLowerCase().includes(query)) {
        allSuggestions.push({ tag_name: tag, source: "preset" });
      }
    });

    // Add user history (exclude preset tags)
    userHistory.forEach((tag) => {
      if (tag.toLowerCase().includes(query) && !presetTags.includes(tag)) {
        allSuggestions.push({ tag_name: tag, source: "user_history" });
      }
    });

    // Add popular tags (exclude preset and user history)
    popularTags.forEach(({ tag_name, count }) => {
      if (
        tag_name.toLowerCase().includes(query) &&
        !presetTags.includes(tag_name) &&
        !userHistory.includes(tag_name)
      ) {
        allSuggestions.push({ tag_name, source: "popular", count });
      }
    });

    // Limit to 20 suggestions
    return allSuggestions.slice(0, 20);
  }, [presetTags, userHistory, popularTags, searchQuery]);

  return { suggestions };
}
